import { LayerExtension } from "@deck.gl/core"
import { degreeSpanForKm } from "@nova/calculate-geospatial"

const MAX_DISKS = 64

export type LightningMaskDisk = { lon: number; lat: number; radiusKm: number }

export type LightningMaskExtensionProps = {
    maskDisks?: LightningMaskDisk[]
}

const merc = (lon: number, lat: number): [number, number] => [
    (lon + 180) / 360,
    0.5 - Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) / (2 * Math.PI),
]

export class LightningMaskExtension extends LayerExtension<LightningMaskExtensionProps> {
    static extensionName = "LightningMaskExtension"
    static defaultProps: LightningMaskExtensionProps = { maskDisks: [] }

    getShaders() {
        return {
            inject: {
                "vs:#decl": `out vec2 vLgtMerc;`,
                "vs:#main-end": `
vLgtMerc = vec2(
    (vertexPositions.x + 180.0) / 360.0,
    0.5 - log(tan(radians(45.0 + clamp(vertexPositions.y, -89.9, 89.9) * 0.5))) / (2.0 * 3.14159265359)
);
`,
                "fs:#decl": `
in vec2 vLgtMerc;
uniform vec4 lgtMask_disks[${MAX_DISKS}]; // xy = center (merc), zw = radii (merc)
uniform int lgtMask_count;
`,
                "fs:#main-start": `
for (int i = 0; i < ${MAX_DISKS}; i++) {
    if (i >= lgtMask_count) break;
    vec2 q = (vLgtMerc - lgtMask_disks[i].xy) / lgtMask_disks[i].zw;
    if (dot(q, q) < 1.0) discard;
}
`,
            },
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    draw(this: any) {
        const disks = (this.props.maskDisks ?? []) as LightningMaskDisk[]
        const count = Math.min(disks.length, MAX_DISKS)
        const packed = new Float32Array(MAX_DISKS * 4)
        for (let i = 0; i < count; i++) {
            const { lon, lat, radiusKm } = disks[i]
            const [cx, cy] = merc(lon, lat)
            const { latitude: latOff, longitude: lonOff } = degreeSpanForKm(radiusKm, lat)
            const rx = Math.abs(merc(lon + lonOff, lat)[0] - cx)
            const ry = Math.abs(merc(lon, lat + latOff)[1] - cy)
            packed.set([cx, cy, rx, ry], i * 4)
        }
        for (const model of this.getModels()) {
            model.setUniforms({ lgtMask_disks: packed, lgtMask_count: count })
        }
    }
}
